/** Freelancer: dashboard, verification deposit, earnings and withdrawals, services, profile. */
import { api } from '../../core/api.js';
import { config } from '../../core/config.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, inr, toPaise } from '../../core/format.js';
import { session } from '../../core/session.js';
import { navigate } from '../../core/router.js';
import { reload } from '../../app/shell.js';
import { av, banner, chips, empty, ic, pageHead, stat, statusTag, table, tag } from '../../ui/components.js';
import { field, inrField, tagField } from '../../ui/forms.js';
import { closeModal, confirmBox, openModal } from '../../ui/modal.js';
import { submitForm } from '../../ui/submit.js';
import { toast } from '../../ui/toast.js';
import { payOrder } from '../billing/pay.js';
import { cofounderCard, loadCofounder } from '../network/cofounder.js';
import { loadResume, resumeCard } from '../resume/card.js';

const quiet = (p, fallback) => p.catch(() => fallback);

/* ---------- dashboard ---------- */

const dashboard = {
  path: '/professional/dashboard',
  roles: ['professional'],
  title: 'Dashboard',
  async load(ctx) {
    const signal = ctx.signal;
    const [deposit, offers, batches, earnings, requirements, projects] = await Promise.all([
      api.get('/billing/deposit', { signal }),
      quiet(api.get('/trainer/offers', { signal }), { offers: [] }),
      quiet(api.get('/trainer/batches', { signal }), { batches: [] }),
      api.get('/freelancer/earnings', { signal }),
      quiet(api.get('/freelancer/requirements', { signal }), { requirements: [] }),
      quiet(api.get('/freelancer/projects', { signal }), { projects: [] }),
    ]);
    return { deposit, offers: offers.offers, batches: batches.batches, earnings, requirements: requirements.requirements, projects: projects.projects };
  },
  view(d) {
    const verified = session.user.verification === 'verified';
    const open = d.requirements.filter((r) => !r.application && r.fit_percent >= 50).length;
    const nextClass = d.batches.map((b) => b.next_class).filter(Boolean).sort((a, b) => a.starts_at.localeCompare(b.starts_at))[0];
    return html`${verified ? '' : banner(html`Your profile is not verified yet. ${d.deposit.deposit ? 'Vignova is checking your profile.' : html`Pay the refundable deposit to start verification. <a href="/professional/deposit">Pay deposit</a>`}`, 'wn', 'shield')}
      ${pageHead(`Welcome, ${session.user.name.split(' ')[0]}`, nextClass ? 'Your next class is coming up.' : 'Teach live batches and take freelance work.', nextClass ? html`<a class="btn" href="/professional/classes/${nextClass.id}">${ic('play')} Open class ${nextClass.number}</a>` : '')}
      <div class="grid stats">${stat(inr(d.earnings.this_month_paise), 'Earned this month')}${stat(inr(d.earnings.available_paise), 'Ready to withdraw')}
        ${stat(d.batches.length, 'Batches you teach')}${stat(d.projects.filter((p) => p.status !== 'completed').length, 'Active projects')}</div>
      <div class="grid g2 mt">
        <div class="card"><h3>Waiting for you</h3><div class="list sm">
          ${d.offers.length ? html`<div class="li">${ic('play')}<span class="f1">${d.offers.length} batch ${d.offers.length === 1 ? 'offer needs' : 'offers need'} an answer</span><a class="btn sm" href="/professional/coaching">Review</a></div>` : ''}
          ${open ? html`<div class="li">${ic('clip')}<span class="f1">${open} ${open === 1 ? 'requirement matches' : 'requirements match'} your skills</span><a class="btn ghost sm" href="/professional/requirements">View</a></div>` : ''}
          ${d.earnings.available_paise > 0 ? html`<div class="li">${ic('wallet')}<span class="f1">${inr(d.earnings.available_paise)} ready to withdraw</span><a class="btn ghost sm" href="/professional/earnings">Withdraw</a></div>` : ''}
          ${!d.offers.length && !open && d.earnings.available_paise <= 0 ? html`<p class="mu" style="margin:0">Nothing needs you right now.</p>` : ''}</div></div>
        <div class="card"><h3>Your status</h3><div class="list sm">
          <div class="li">${ic('shield')}<span class="f1">Verification</span>${verified ? tag('Verified', 'ok') : tag('Not verified', 'wn')}</div>
          <div class="li">${ic('wallet')}<span class="f1">Deposit ${inr(d.deposit.required_paise)}</span>${d.deposit.deposit ? statusTag(d.deposit.deposit.display_status === 'Refunded' ? 'refunded' : d.deposit.deposit.status) : tag('Not paid', 'wn')}</div></div></div>
      </div>`;
  },
};

/* ---------- deposit ---------- */

const deposit = {
  path: '/professional/deposit',
  roles: ['professional'],
  nav: 'dashboard',
  title: 'Verification deposit',
  ui: () => ({ error: '' }),
  load: (ctx) => api.get('/billing/deposit', { signal: ctx.signal }),
  view(d, ui) {
    if (d.deposit) {
      return html`<div class="card" style="max-width:600px">${banner(html`<b>Your deposit is recorded.</b> Status: ${d.deposit.display_status}.${d.deposit.valid_until ? ` Refund review after ${fmtDate(d.deposit.valid_until)}.` : ''}`, 'good', 'check')}
        <a class="btn mt" href="/professional/dashboard">Go to dashboard</a></div>`;
    }
    return html`<div class="card" style="max-width:640px;margin:auto"><h2>Refundable verification deposit</h2>
      <div class="stat"><div class="k">${inr(d.required_paise)}</div><div class="l">Refundable, held for ${d.hold_days} days</div></div>
      <p class="mt-s">This deposit keeps spam, fake profiles and unserious sign-ups off VConnect. It is refunded after the verification period if there are no policy violations.</p>
      ${banner('The deposit does not guarantee projects, students or earnings.', '', 'info')}
      <div class="list mt sm"><div class="li">${ic('shield')} Identity and profile checked by Vignova</div><div class="li">${ic('refresh')} Refund to your original payment method</div><div class="li">${ic('ban')} Kept only if you break the community rules</div></div>
      <form data-submit="deposit.pay" class="mt" novalidate><label class="row sm" style="flex-wrap:nowrap;align-items:flex-start;gap:8px"><input type="checkbox" name="understood"> <span>I understand the deposit is refundable and does not guarantee work.</span></label>
        ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}<button class="btn lg wide mt-s" type="submit">Pay ${inr(d.required_paise)} deposit</button></form>
      <p class="note mt-s">${ic('lock')} ${config.paymentMode === 'manual' ? 'You pay by UPI or bank transfer and Vignova confirms it.' : 'Payments are processed by Razorpay.'}</p></div>`;
  },
};

register({
  'deposit.pay': (form) => submitForm(form, async ({ understood }) => {
    if (!understood) throw new Error('Tick the box to confirm you understand.');
    const { order } = await api.post('/billing/deposit');
    const user = session.user;
    const result = await payOrder(order, { description: 'Verification deposit', prefill: { name: user.name, email: user.email, contact: user.phone } });
    if (result.status === 'paid') {
      toast('Deposit received. Vignova will verify your profile.', 'shield');
      await reload();
      await navigate('/professional/dashboard');
    } else if (result.status === 'pending') {
      await navigate('/professional/dashboard');
    } else if (result.status === 'failed') throw new Error(`${result.message} You have not been charged.`);
    else toast('Payment cancelled. You have not been charged.', 'alert');
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- earnings ---------- */

const earnings = {
  path: '/professional/earnings',
  roles: ['professional'],
  title: 'Earnings',
  load: (ctx) => api.get('/freelancer/earnings', { signal: ctx.signal }),
  view(d) {
    return html`<div class="grid stats">${stat(inr(d.this_month_paise), 'This month')}${stat(inr(d.lifetime_paise), 'Lifetime')}${stat(inr(d.pending_paise), 'Pending release')}${stat(inr(d.available_paise), 'Ready to withdraw')}</div>
      <div class="row mt">${d.available_paise > 0 ? html`<button type="button" class="btn" data-act="earn.withdraw" data-max="${d.available_paise}">${ic('wallet')} Withdraw</button>` : html`<span class="mu sm">Nothing to withdraw yet.</span>`}</div>
      <h3 class="mt">Earnings</h3>${table(['Date', 'Source', 'Amount', 'Available from'], d.entries.map((e) => [fmtDate(e.at), e.description, inr(e.amount_paise), e.available_at ? fmtDate(e.available_at) : '']), { empty: 'Earnings from classes you teach appear here.' })}
      <h3 class="mt">Withdrawals</h3>${table(['Requested', 'To', 'Amount', 'Status'], d.withdrawals.map((w) => [fmtDate(w.requested_at), w.destination_label, inr(w.amount_paise), statusTag(w.status)]), { empty: 'No withdrawals yet.' })}
      <p class="note mt-s">Payouts are made by Vignova to your bank or UPI after review, usually within 2 working days.</p>`;
  },
};

register({
  'earn.withdraw': (el) => openModal({
    title: 'Withdraw',
    body: html`<form id="withdraw-form" data-submit="earn.withdraw.send" novalidate>${inrField('wd-amount', 'Amount', { name: 'amount', number: true, val: Math.floor(Number(el.dataset.max) / 100), help: `Up to ${inr(el.dataset.max)}.` })}
      ${field('wd-to', 'Send to', { name: 'destination_label', ph: 'HDFC Bank 4821, or UPI name@bank', minlen: 3, maxlength: 120 })}</form><p class="note">A payout is made after Vignova reviews the request. No fee.</p>`,
    foot: html`<button type="button" class="btn ghost" data-act="modal.close">Cancel</button><button type="submit" form="withdraw-form" class="btn">Request withdrawal</button>`,
  }),
  'earn.withdraw.send': (form) => submitForm(form, async ({ amount, destination_label: destination }) => {
    const paise = toPaise(amount);
    if (!Number.isFinite(paise) || paise < 100) throw new Error('Enter an amount of at least ₹1.');
    await api.post('/freelancer/withdrawals', { body: { amount_paise: paise, destination_label: destination } });
    closeModal(false);
    toast('Withdrawal requested', 'wallet');
    await reload();
  }, { onError: (error) => toast(error.message, 'alert') }),
});

/* ---------- services ---------- */

const services = {
  path: '/professional/services',
  roles: ['professional'],
  title: 'Services',
  load: (ctx) => api.get('/freelancer/services', { signal: ctx.signal }),
  view(d) {
    return html`${pageHead('My services', 'What clients and Vignova can match you to. Rates are per hour.')}
      ${d.services.length ? html`<div class="grid g3">${d.services.map((s) => html`<div class="card"><div class="row between"><h3 style="margin:0">${s.name}</h3>${s.active ? tag('Active', 'ok') : tag('Paused', 'n')}</div>
        <p class="mt-s"><b>${inr(s.rate_paise)}</b> <span class="mu sm">per hour</span></p>
        <div class="row"><button type="button" class="btn ghost sm" data-act="svc.toggle" data-id="${s.id}" data-name="${s.name}" data-rate="${s.rate_paise}" data-active="${!s.active}">${s.active ? 'Pause' : 'Resume'}</button>
          <button type="button" class="btn danger sm" data-act="svc.remove" data-id="${s.id}" data-name="${s.name}">Remove</button></div></div>`)}</div>` : empty('grid', 'No services yet', 'Add what you offer so you can be matched to work.')}
      <div class="card mt" style="max-width:560px"><h3>Add a service</h3><form data-submit="svc.add" novalidate><div class="fgrid">${field('sv-n', 'Service', { name: 'name', ph: 'Test automation', minlen: 2, maxlength: 100 })}${inrField('sv-r', 'Rate per hour', { name: 'rate', number: true, ph: '700' })}</div>
        <button class="btn" type="submit">${ic('plus')} Add service</button></form></div>`;
  },
};

register({
  'svc.add': (form) => submitForm(form, async ({ name, rate }) => {
    await api.post('/freelancer/services', { body: { name, rate_paise: toPaise(rate), active: true } });
    form.reset();
    toast('Service added');
    await reload();
  }),
  'svc.toggle': async (el) => {
    await api.put(`/freelancer/services/${el.dataset.id}`, { body: { name: el.dataset.name, rate_paise: Number(el.dataset.rate), active: el.dataset.active === 'true' } });
    await reload();
  },
  'svc.remove': async (el) => {
    if (!(await confirmBox({ title: `Remove ${el.dataset.name}?`, text: 'You will stop getting matched to this service.', label: 'Remove', danger: true }))) return;
    await api.delete(`/freelancer/services/${el.dataset.id}`);
    await reload();
  },
});

/* ---------- profile ---------- */

const profile = {
  path: '/professional/profile',
  roles: ['professional'],
  title: 'My profile',
  async load(ctx) {
    const [p, cofounder, resume] = await Promise.all([api.get('/freelancer/profile', { signal: ctx.signal }), loadCofounder(ctx.signal), loadResume(ctx.signal)]);
    return { profile: p.profile, cofounder, resume };
  },
  view(d) {
    const p = d.profile;
    return html`<div class="card"><div class="row nw">${av(session.user.name, 'lg')}<div class="f1"><h2 style="margin:0">${session.user.name}</h2>
      <p class="mu" style="margin:0">${p.headline || 'Add a headline below'}${p.years_exp ? `, ${p.years_exp}+ years` : ''}</p></div>${p.verification === 'verified' ? tag('Verified', 'ok') : tag('Not verified', 'wn')}</div>
      ${p.bio ? html`<p class="mt-s">${p.bio}</p>` : ''}${chips(p.skills)}</div>
      <div class="grid g2 mt" style="align-items:start"><div class="card"><h3>Edit profile</h3><form data-submit="fprofile.save" novalidate>
        ${field('fp-head', 'Headline', { name: 'headline', val: p.headline, maxlength: 200, ph: 'Senior QA engineer, API and automation' })}
        <div class="fgrid">${field('fp-role', 'Current role', { name: 'current_role', val: p.current_role, req: false, maxlength: 120 })}${field('fp-co', 'Company', { name: 'company', val: p.company, req: false, maxlength: 120 })}
        ${field('fp-exp', 'Years of experience', { type: 'number', name: 'years_exp', min: 0, max: 60, val: p.years_exp ?? '', req: false })}${field('fp-city', 'City', { name: 'city', val: p.city, req: false, maxlength: 80 })}</div>
        ${tagField('fp-skills', 'Skills', { name: 'skills', preset: p.skills, req: false })}
        ${field('fp-teach', 'Courses you can teach', { name: 'can_teach', val: p.can_teach, req: false, maxlength: 300 })}
        ${field('fp-bio', 'About you', { name: 'bio', rows: 4, val: p.bio, req: false, max: 3000 })}
        <div class="fgrid">${field('fp-avail', 'Availability', { name: 'availability', val: p.availability, req: false, maxlength: 200, ph: 'Weekday evenings' })}${inrField('fp-rate', 'Default hourly rate', { name: 'rate', number: true, val: p.hourly_rate_paise ? p.hourly_rate_paise / 100 : '', req: false })}</div>
        <button class="btn" type="submit">Save profile</button></form></div>${resumeCard(d.resume)}</div>
      <div class="mt">${cofounderCard(d.cofounder)}</div>`;
  },
};

register({
  'fprofile.save': (form) => submitForm(form, async (v) => {
    const body = { headline: v.headline, current_role: v.current_role, company: v.company, city: v.city, skills: v.skills, can_teach: v.can_teach, bio: v.bio, availability: v.availability };
    if (v.years_exp !== '') body.years_exp = Number(v.years_exp);
    if (v.rate !== '') body.hourly_rate_paise = toPaise(v.rate);
    await api.put('/freelancer/profile', { body });
    toast('Profile saved');
    await reload();
  }),
});

export const routes = [dashboard, deposit, earnings, services, profile];
